import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Container,
    IconButton,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { InfoOutlined, Print, Refresh, Visibility } from "@material-ui/icons"
import { format } from "date-fns"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
    AceitarPedido,
    buscarInformacoesPedido,
    buscarPedidosListagem,
    CancelarPedido,
    DespacharPedido,
    FinalizarPedido,
    PrepararPedido,
} from "../../services/integracao"
import useAppStore from "../../stores/AppStore"
import { useSnackbar } from "../../utils/notificacoes"
import Cabecalho from "../Cabecalho"
import { corStatusPill, formatarMoeda } from "../HistoricoPedidos/constants"
import { verificarPrintQRCode } from "../RecebedorDePedidos/functions"
import DialogHistoricoPedido from "./DialogHistoricoPedido"
import DialogVerPedido from "./DialogVerPedido"
import { useStreamPedidosCompartilhado } from "./useStreamPedidosCompartilhado"

const AGUARDANDO = "AGUARDANDO"
const PREPARO = "PREPARO"
const ENTREGA = "ENTREGA"
const FINALIZADO = "FINALIZADO"

// Espelha o mapeamento de status do app (stores/PedidosStore.ts).
function grupoStatus(status) {
    if (status === 0) return AGUARDANDO
    if (status === 3 || status === 6) return ENTREGA
    if ([4, 5, -1, -2, -3, -4, -10].includes(status)) return FINALIZADO
    return PREPARO // 1, 2, 7 e demais "em preparação"
}

// Ações permitidas por status. As validações de transição são do backend
// (atualizarStatusPedido); aqui só oferecemos os botões contextuais.
function acoesDoPedido(status) {
    if (status === 0)
        return [
            { label: "Aceitar", tipo: "aceitar" },
            { label: "Cancelar", tipo: "cancelar" },
        ]
    if (status === 1 || status === 2)
        return [
            { label: "Preparar", tipo: "preparar" },
            { label: "Saiu p/ entrega", tipo: "despachar" },
            { label: "Cancelar", tipo: "cancelar" },
        ]
    if (grupoStatus(status) === PREPARO)
        return [
            { label: "Saiu p/ entrega", tipo: "despachar" },
            { label: "Cancelar", tipo: "cancelar" },
        ]
    if (grupoStatus(status) === ENTREGA) return [{ label: "Finalizar", tipo: "finalizar" }]
    return []
}

// Início da janela: as 06:00 mais recentes antes de agora
// (se agora >= 06:00 -> hoje 06:00; senão -> ontem 06:00).
function inicioSeisHoras() {
    const agora = new Date()
    const inicio = new Date(agora)
    inicio.setHours(6, 0, 0, 0)
    if (agora.getHours() < 6) inicio.setDate(inicio.getDate() - 1)
    return inicio
}

const FMT_API = "yyyy-MM-dd HH:mm:ss"

const useStyles = makeStyles(theme => ({
    backdrop: { zIndex: theme.zIndex.drawer + 999999, color: "#fff" },
    // Compensa a sidebar fixa (mini drawer de 80px) do Cabeçalho, visível a
    // partir de lg; abaixo disso o menu vira temporário e o conteúdo ocupa tudo.
    container: {
        paddingTop: "6em",
        paddingBottom: "2em",
        [theme.breakpoints.up("lg")]: {
            marginLeft: 80,
            width: "calc(100% - 80px)",
        },
    },
    resumo: {
        display: "flex",
        gap: 32,
        flexWrap: "wrap",
        color: "#555",
        marginBottom: 16,
    },
    row: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: "#fff",
        borderRadius: 8,
        padding: "8px 14px",
        marginBottom: 10,
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        cursor: "pointer",
        transition: "box-shadow .15s",
        "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.18)" },
    },
    icone: { color: "#607d8b" },
    valorBox: {
        background: "#eceff1",
        borderRadius: 6,
        padding: "6px 10px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: "#37474f",
    },
    rowTexto: { flex: 1, minWidth: 0 },
    statusBtn: {
        color: "#fff",
        whiteSpace: "nowrap",
        [theme.breakpoints.down("xs")]: { display: "none" },
    },
}))

export default function PedidosDoDia() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const autorizacao = localStorage.getItem("chaveSistema")

    const estabelecimentoAtual = useAppStore(
        s => s.configuracoes?.user?.parametros?.estabelecimento
    )
    const idEstabelecimento = estabelecimentoAtual?.id

    // Só os perfis completos veem o faturamento (Valor total).
    const perfilUsuario = useAppStore(s => s.configuracoes?.user?.perfil)
    const podeVerValorTotal = perfilUsuario === "ADMINISTRADOR" || perfilUsuario === "CLIENTEFIEL"

    const [pedidos, setPedidos] = useState([])
    const [loading, setLoading] = useState(false)
    const [erro, setErro] = useState("")
    const [atualizadoEm, setAtualizadoEm] = useState(null)
    const [historicoPedidoId, setHistoricoPedidoId] = useState(null)
    const [pedidoVisualizado, setPedidoVisualizado] = useState(null)

    const alertar = useCallback(
        (msg, tipo) => enqueueSnackbar(msg, { variant: tipo }),
        [enqueueSnackbar]
    )

    const carregar = useCallback(async () => {
        if (!idEstabelecimento) return
        setLoading(true)
        const filtros = {
            campoData: "data",
            dataInicio: format(inicioSeisHoras(), FMT_API),
            dataFim: format(new Date(), FMT_API),
            page: 1,
            size: 500,
        }
        const resp = await buscarPedidosListagem(idEstabelecimento, filtros, autorizacao)
        setLoading(false)
        if (resp.retornoErro) {
            setErro(resp.mensagem || "Erro ao buscar pedidos.")
            return
        }
        setErro("")
        setPedidos(resp.pedidos || [])
        setAtualizadoEm(new Date())
    }, [idEstabelecimento, autorizacao])

    // Carga inicial e quando troca o estabelecimento.
    useEffect(() => {
        if (idEstabelecimento) carregar()
    }, [idEstabelecimento, carregar])

    // Ao abrir a tela, conecta o QZ-Tray e popula as impressoras (mesma
    // preparação do Recebedor). Sem isso, a primeira impressão cairia no modo
    // "navegador"; com as impressoras carregadas, sai direto na impressora.
    const buscarImpressoras = useAppStore(s => s.buscarImpressoras)
    useEffect(() => {
        buscarImpressoras()
    }, [buscarImpressoras])

    // Impressão manual (botão de impressora na linha): busca o detalhe completo
    // e envia para a impressora, sem a trava anti-reimpressão.
    const imprimirPedidoManual = useCallback(
        async p => {
            const info = (await buscarInformacoesPedido(autorizacao, p.id)) || p
            const { mandarImprimir } = useAppStore.getState()
            mandarImprimir({
                pedido: info,
                texto: info.texto,
                qtd: 1,
                qrcode: verificarPrintQRCode(info),
                imprimirNota: true,
                reimpressaoManual: true,
            })
        },
        [autorizacao]
    )

    // Atualização cirúrgica ao receber um evento SSE: busca só o pedido alterado
    // e faz upsert (mesma shape DetalhesPedidoRetorno da listagem).
    const aoReceberEvento = useCallback(
        async evento => {
            if (!evento?.pedidoId) return
            // O stream é da conta inteira (todos os estabelecimentos do fornecedor).
            // Esta tela lista um único estabelecimento (buscarPedidosListagem por id),
            // então só reage aos eventos DELE.
            if (idEstabelecimento && evento.estabelecimentoId !== idEstabelecimento) return
            // A animação da bandeja e o aceite/impressão automático (modo "Sempre")
            // são tratados pelo Cabeçalho, que assina o mesmo stream SSE globalmente.
            // Aqui apenas atualizamos os cards da tela.
            const info = await buscarInformacoesPedido(autorizacao, evento.pedidoId)
            if (!info?.id) return
            setPedidos(prev => {
                const existe = prev.some(p => p.id === info.id)
                return existe ? prev.map(p => (p.id === info.id ? info : p)) : [info, ...prev]
            })
            setAtualizadoEm(new Date())
        },
        [autorizacao, idEstabelecimento]
    )

    useStreamPedidosCompartilhado(autorizacao, aoReceberEvento)

    const executarAcao = useCallback(
        async (pedido, tipo) => {
            let dados
            if (tipo === "cancelar") {
                const observacao = window.prompt("Motivo do cancelamento:")
                if (observacao === null) return
                dados = { observacao, motivoCancelamentoPedido: observacao }
            }
            setLoading(true)
            let resultado
            try {
                if (tipo === "aceitar") resultado = await AceitarPedido(pedido.id)
                else if (tipo === "preparar") resultado = await PrepararPedido(pedido.id)
                else if (tipo === "despachar") resultado = await DespacharPedido(pedido.id)
                else if (tipo === "finalizar") resultado = await FinalizarPedido(pedido.id)
                else if (tipo === "cancelar") resultado = await CancelarPedido(pedido.id, dados)
            } catch (e) {
                /* tratado abaixo */
            }
            setLoading(false)
            if (resultado?.id) {
                setPedidos(prev => prev.map(p => (p.id === resultado.id ? resultado : p)))
                setAtualizadoEm(new Date())
                alertar("Status atualizado.", "success")
            } else {
                alertar("Não foi possível atualizar o status.", "error")
            }
        },
        [alertar]
    )

    // Lista única, todos os status juntos, mais recentes primeiro.
    const listaOrdenada = useMemo(
        () => [...pedidos].sort((a, b) => (b.id || 0) - (a.id || 0)),
        [pedidos]
    )
    const totalValor = useMemo(
        () => listaOrdenada.reduce((s, p) => s + (p.totalPrice || 0), 0),
        [listaOrdenada]
    )

    return (
        <Container maxWidth={false} className={classes.container}>
            <Cabecalho pagina={"Pedidos do Dia"} />

            <Backdrop open={loading} className={classes.backdrop}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                mb={2}
                style={{ gap: 12 }}
            >
                <Typography variant="h5">Pedidos do Dia</Typography>
                <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                    {atualizadoEm && (
                        <Typography variant="caption" color="textSecondary">
                            Atualizado {atualizadoEm.toLocaleTimeString("pt-BR")}
                        </Typography>
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Refresh />}
                        onClick={carregar}
                    >
                        Atualizar
                    </Button>
                </Box>
            </Box>

            {erro && (
                <Typography color="error" variant="body2" style={{ marginBottom: 12 }}>
                    {erro}
                </Typography>
            )}

            <Box className={classes.resumo}>
                <span>
                    Quantidade de pedidos: <b>{listaOrdenada.length}</b>
                </span>
                {podeVerValorTotal && (
                    <span>
                        Valor total: <b>{formatarMoeda(totalValor)}</b>
                    </span>
                )}
            </Box>

            {listaOrdenada.length === 0 && !loading && (
                <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
                    Nenhum pedido.
                </Typography>
            )}

            {listaOrdenada.map(p => {
                const pillStatus = corStatusPill(p.descricaoStatus)
                const horaTexto = (p.dateFormatted || "").includes("Hora:")
                    ? "Hora: " + p.dateFormatted.split("Hora:")[1].trim()
                    : p.dateFormatted || ""
                return (
                    <div key={p.id} className={classes.row} onClick={() => setPedidoVisualizado(p)}>
                        <IconButton
                            size="small"
                            className={classes.icone}
                            title="Histórico"
                            onClick={e => {
                                e.stopPropagation()
                                setHistoricoPedidoId(p.id)
                            }}
                        >
                            <InfoOutlined />
                        </IconButton>
                        <IconButton
                            size="small"
                            className={classes.icone}
                            title="Ver pedido"
                            onClick={e => {
                                e.stopPropagation()
                                setPedidoVisualizado(p)
                            }}
                        >
                            <Visibility />
                        </IconButton>
                        <IconButton
                            size="small"
                            className={classes.icone}
                            title="Imprimir"
                            onClick={e => {
                                e.stopPropagation()
                                imprimirPedidoManual(p)
                            }}
                        >
                            <Print />
                        </IconButton>

                        <span className={classes.valorBox}>{formatarMoeda(p.totalPrice)}</span>

                        <Typography variant="body2" className={classes.rowTexto} noWrap>
                            <b>#{p.id}</b>
                            {horaTexto ? `  ${horaTexto}` : ""}
                            {`  -  ${p.customer?.name || "—"}`}
                            {p.payments?.name ? `  -  ${p.payments.name}` : ""}
                        </Typography>

                        <Button
                            size="small"
                            variant="contained"
                            className={classes.statusBtn}
                            style={{ background: pillStatus.bg, color: pillStatus.color }}
                            onClick={e => {
                                e.stopPropagation()
                                setPedidoVisualizado(p)
                            }}
                        >
                            {p.descricaoStatus || "—"}
                        </Button>
                    </div>
                )
            })}

            <DialogHistoricoPedido
                aberto={!!historicoPedidoId}
                pedidoId={historicoPedidoId}
                autorizacao={autorizacao}
                onClose={() => setHistoricoPedidoId(null)}
            />

            <DialogVerPedido
                pedido={pedidoVisualizado}
                acoes={pedidoVisualizado ? acoesDoPedido(pedidoVisualizado.status) : []}
                autorizacao={autorizacao}
                onAcao={executarAcao}
                onClose={() => setPedidoVisualizado(null)}
            />
        </Container>
    )
}
